import {
    Card,
    Heading,
    Pagination,
    Paragraph,
    type SuggestionItem,
    Table,
    Tag,
    Tooltip,
    usePagination,
} from "@digdir/designsystemet-react";
import { useDeferredValue, useEffect, useMemo, useRef, useState, useTransition } from "react";
import { AreaContainer } from "../shared/AreaContainer";
import { InfoTooltip } from "../shared/InfoTooltip";
import "./Prosjektliste.css";
import { InformationIcon } from "@navikt/aksel-icons";
import { Dialog } from "../shared/Dialog";
import { FlexContainer } from "../shared/FlexContainer";
import { downloadProsjektCsv } from "./data/csvDownload";
import { downloadProsjektExcel } from "./data/excelDownload";
import {
    getConfiguredDataSource,
    getGroupedFilterConfigForDataSource,
    getProsjekterForDataSource,
} from "./data/prosjektlisteDataSource";
import type { ProsjektRow } from "./data/types";
import { Filter } from "./filter/Filter";
import {
    type FilterKey,
    filterDefinitions,
    filterKeys,
    groupedFilterLabelByValueByKey,
    normalizeStatusValue,
    statusOptionOrder,
} from "./filter/filterDefinitions";
import {
    type GroupedSuggestionConfig,
    getGroupedSuggestionOptions,
    normalizeGroupedSelection,
} from "./filter/groupedSuggestion";
import {
    formatBelop,
    formatCardNumber,
    formatVedtaksaar,
    getCancelledCellTooltip,
    getDefaultSortDirectionForSortKey,
    getYearFromTimestamp,
    normalizeSearchValue,
    shouldShowOriginalSupportAmount,
} from "./prosjektliste-utils";

function normalizeFilterSelection(
    groupedConfig: GroupedSuggestionConfig | undefined,
    values: SuggestionItem[],
    previousValues: SuggestionItem[] = [],
): SuggestionItem[] {
    if (!groupedConfig) {
        return values;
    }

    return normalizeGroupedSelection(groupedConfig, values, previousValues);
}

const MOCK_PAGE_SIZE = 5;
const BACKEND_PAGE_SIZE = 50;
const YEAR_SLIDER_MIN = 2017;

type SortKey = Exclude<keyof ProsjektRow, "url">;
type SortDirection = "asc" | "desc";

const defaultSortKey: SortKey = "vedtaksdato";

const defaultSortDirection: SortDirection = getDefaultSortDirectionForSortKey(defaultSortKey);

const searchQueryParamKey = "q";
const sortQueryParamKey = "sort";
const sortDirectionQueryParamKey = "dir";
const statusOptionRankByValue = new Map<string, number>(statusOptionOrder.map((value, index) => [value, index]));

const emptyFilterState: Record<FilterKey, SuggestionItem[]> = filterKeys.reduce(
    (result, key) => {
        result[key] = [];
        return result;
    },
    {} as Record<FilterKey, SuggestionItem[]>,
);

type SortableColumn = {
    key: SortKey;
    label: string;
    shortLabel: string;
    isNumeric?: boolean;
    tooltipText?: string;
};

const sortableColumns: SortableColumn[] = [
    { key: "vedtaksdato", label: "Vedtaksår", shortLabel: "Vedtak" },
    { key: "prosjekttittel", label: "Prosjekttittel", shortLabel: "Prosjekttittel" },
    { key: "prosjekteier", label: "Prosjekteier", shortLabel: "Prosjekteier" },
    {
        key: "stottebelop",
        label: "Støttebeløp (kr)",
        shortLabel: "Støtte (kr)",
        isNumeric: true,
    },
    {
        key: "levetid",
        label: "Levetid (år)",
        shortLabel: "Levetid",
        isNumeric: true,
        tooltipText: "Forventet teknisk levetid for prosjektet på søknadstidspunkt",
    },
    {
        key: "utslippsreduksjon",
        label: "Utslippsreduksjon (kg CO₂e)",
        shortLabel: "Utslippsred. (kg CO₂e)",
        isNumeric: true,
        tooltipText: "Forventet utslippsreduksjon over levetid, oppgitt i kg CO₂e (CO₂-ekvivalenter)",
    },
    {
        key: "energiresultat",
        label: "Energiresultat (kWh)",
        shortLabel: "Energires. (kWh)",
        isNumeric: true,
        tooltipText: "Forventet energiresultat, oppgitt i kWh",
    },
    {
        key: "stotteobjekter",
        label: "Støtteobjekter",
        shortLabel: "Støtteobj.",
        isNumeric: true,
        tooltipText: "Antall støtteobjekter i prosjektet",
    },
];

const sortableColumnKeys = new Set<SortKey>(sortableColumns.map((column) => column.key));

const numericSortKeys: SortKey[] = ["stottebelop", "levetid", "utslippsreduksjon", "energiresultat", "stotteobjekter"];
const tableNumberFormatter = new Intl.NumberFormat("nb-NO");

function getInitialFiltersFromUrl(): Record<FilterKey, SuggestionItem[]> {
    if (typeof window === "undefined") {
        return { ...emptyFilterState };
    }

    const params = new URLSearchParams(window.location.search);

    return filterKeys.reduce<Record<FilterKey, SuggestionItem[]>>(
        (result, key) => {
            const values = params.getAll(filterDefinitions[key].queryKey);
            const labelByValue = groupedFilterLabelByValueByKey[key];
            result[key] = values.map((value) => ({
                label: labelByValue?.get(value) ?? (key === "Status" ? normalizeStatusValue(value) : value),
                value: key === "Status" ? normalizeStatusValue(value) : value,
            }));
            return result;
        },
        { ...emptyFilterState },
    );
}

function getInitialSearchTermFromUrl(): string {
    if (typeof window === "undefined") {
        return "";
    }

    const params = new URLSearchParams(window.location.search);
    return params.get(searchQueryParamKey) ?? "";
}

function getInitialSortKeyFromUrl(): SortKey {
    if (typeof window === "undefined") {
        return defaultSortKey;
    }

    const params = new URLSearchParams(window.location.search);
    const urlSortKey = params.get(sortQueryParamKey);

    if (urlSortKey && sortableColumnKeys.has(urlSortKey as SortKey)) {
        return urlSortKey as SortKey;
    }

    return defaultSortKey;
}

function getInitialSortDirectionFromUrl(): SortDirection {
    if (typeof window === "undefined") {
        return defaultSortDirection;
    }

    const params = new URLSearchParams(window.location.search);
    const sortDirection = params.get(sortDirectionQueryParamKey);

    if (sortDirection === "asc" || sortDirection === "desc") {
        return sortDirection;
    }

    const urlSortKey = params.get(sortQueryParamKey);
    if (urlSortKey && sortableColumnKeys.has(urlSortKey as SortKey)) {
        return getDefaultSortDirectionForSortKey(urlSortKey as SortKey);
    }

    return defaultSortDirection;
}

function writeTableStateToUrl({
    selectedFilters,
    searchTerm,
    sortKey,
    sortDirection,
}: {
    selectedFilters: Record<FilterKey, SuggestionItem[]>;
    searchTerm: string;
    sortKey: SortKey;
    sortDirection: SortDirection;
}) {
    if (typeof window === "undefined") {
        return;
    }

    const params = new URLSearchParams(window.location.search);

    for (const key of filterKeys) {
        const queryKey = filterDefinitions[key].queryKey;
        params.delete(queryKey);
        selectedFilters[key].forEach((item) => {
            params.append(queryKey, item.value);
        });
    }

    params.delete(searchQueryParamKey);
    if (searchTerm.trim().length > 0) {
        params.set(searchQueryParamKey, searchTerm);
    }

    params.delete(sortQueryParamKey);
    params.delete(sortDirectionQueryParamKey);

    if (sortKey !== defaultSortKey) {
        params.set(sortQueryParamKey, sortKey);
    }
    if (sortDirection !== defaultSortDirection) {
        params.set(sortDirectionQueryParamKey, sortDirection);
    }

    const query = params.toString();
    const nextUrl =
        query.length > 0
            ? `${window.location.pathname}?${query}${window.location.hash}`
            : `${window.location.pathname}${window.location.hash}`;

    window.history.replaceState(null, "", nextUrl);
}

function Prosjektliste() {
    const dataSource = useMemo(getConfiguredDataSource, []);
    const prosjekter = useMemo(() => getProsjekterForDataSource(dataSource), [dataSource]);
    const PAGE_SIZE = dataSource === "backend" ? BACKEND_PAGE_SIZE : MOCK_PAGE_SIZE;
    const groupedFilterConfigByKey = useMemo(
        () =>
            filterKeys.reduce<Partial<Record<FilterKey, GroupedSuggestionConfig>>>((result, key) => {
                const config = getGroupedFilterConfigForDataSource(dataSource, key);
                if (config) {
                    result[key] = config;
                }
                return result;
            }, {}),
        [dataSource],
    );

    const [, startTransition] = useTransition();

    const [sortKey, setSortKey] = useState<SortKey>(() => getInitialSortKeyFromUrl());
    const [sortDirection, setSortDirection] = useState<SortDirection>(() => getInitialSortDirectionFromUrl());
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState(() => getInitialSearchTermFromUrl());
    const deferredSearchTerm = useDeferredValue(searchTerm);
    const [selectedFilters, setSelectedFilters] = useState<Record<FilterKey, SuggestionItem[]>>(() =>
        getInitialFiltersFromUrl(),
    );
    const [yearRange, setYearRange] = useState<{ fromYear: number | null; toYear: number | null }>({
        fromYear: null,
        toYear: null,
    });

    const minAvailableYear = YEAR_SLIDER_MIN;
    const maxAvailableYear = new Date().getUTCFullYear();

    useEffect(() => {
        setYearRange((previousRange) => {
            const boundedFromYear =
                previousRange.fromYear === null
                    ? minAvailableYear
                    : Math.min(Math.max(previousRange.fromYear, minAvailableYear), maxAvailableYear);
            const boundedToYear =
                previousRange.toYear === null
                    ? maxAvailableYear
                    : Math.min(Math.max(previousRange.toYear, minAvailableYear), maxAvailableYear);

            return {
                fromYear: Math.min(boundedFromYear, boundedToYear),
                toYear: Math.max(boundedFromYear, boundedToYear),
            };
        });
    }, [maxAvailableYear]);

    useEffect(() => {
        writeTableStateToUrl({ selectedFilters, searchTerm, sortKey, sortDirection });
    }, [selectedFilters, searchTerm, sortKey, sortDirection]);

    const filterOptions = useMemo(() => {
        return filterKeys.reduce<Record<FilterKey, SuggestionItem[]>>(
            (result, key) => {
                const groupedConfig = groupedFilterConfigByKey[key];
                if (groupedConfig) {
                    result[key] = getGroupedSuggestionOptions(groupedConfig);
                    return result;
                }

                const values = Array.from(new Set(prosjekter.map((row) => row[key])))
                    .map((value) => (key === "Status" ? normalizeStatusValue(value) : value))
                    .filter((value) => {
                        if (value.trim().length === 0) {
                            return false;
                        }

                        if (value === "Ukjent") {
                            return false;
                        }

                        return true;
                    })
                    .filter((value, index, array) => array.indexOf(value) === index)
                    .sort((a, b) => {
                        if (key === "Status") {
                            const aRank = statusOptionRankByValue.get(a) ?? Number.MAX_SAFE_INTEGER;
                            const bRank = statusOptionRankByValue.get(b) ?? Number.MAX_SAFE_INTEGER;

                            if (aRank !== bRank) {
                                return aRank - bRank;
                            }
                        }

                        return a.localeCompare(b, "nb");
                    });
                result[key] = values.map((value) => ({ label: value, value }));
                return result;
            },
            { ...emptyFilterState },
        );
    }, [groupedFilterConfigByKey, prosjekter]);

    const stotteobjektBreakdownBySaksnr = useMemo(() => {
        const bySaksnr = new Map<string, Map<string, number>>();

        for (const row of prosjekter) {
            const saksnr = row.saksnr;
            const stotteobjektType = row.Støtteobjekt?.trim();
            const antall = Number(row.stotteobjekter);

            if (!saksnr || !stotteobjektType || !Number.isFinite(antall) || antall <= 0) {
                continue;
            }

            const byType = bySaksnr.get(saksnr) ?? new Map<string, number>();
            byType.set(stotteobjektType, (byType.get(stotteobjektType) ?? 0) + antall);
            bySaksnr.set(saksnr, byType);
        }

        return new Map(
            Array.from(bySaksnr.entries()).map(([saksnr, byType]) => [
                saksnr,
                Array.from(byType.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "nb")),
            ]),
        );
    }, [prosjekter]);

    const filteredProsjekter = useMemo(() => {
        const searchNeedle = normalizeSearchValue(deferredSearchTerm);

        return prosjekter.filter((row) => {
            const rowVedtaksaar = getYearFromTimestamp(row.vedtaksdato);
            const matchesYearRange =
                (yearRange.fromYear === null || (rowVedtaksaar !== null && rowVedtaksaar >= yearRange.fromYear)) &&
                (yearRange.toYear === null || (rowVedtaksaar !== null && rowVedtaksaar <= yearRange.toYear));

            if (!matchesYearRange) {
                return false;
            }

            const matchesFilters = filterKeys.every((key) => {
                const selected = selectedFilters[key];
                if (selected.length === 0) {
                    return true;
                }

                if (key === "Støtteobjekt") {
                    const hovedkategori = row.stotteobjektHovedkategori ?? "";
                    const underkategori = row.stotteobjektUnderkategori ?? "";

                    return selected.some((item) => {
                        if (item.value === row[key] || item.value === hovedkategori) {
                            return true;
                        }

                        if (item.value.includes("::")) {
                            const [selectedHovedkategori, selectedUnderkategori] = item.value.split("::");
                            return hovedkategori === selectedHovedkategori && underkategori === selectedUnderkategori;
                        }

                        return false;
                    });
                }

                return selected.some((item) => item.value === row[key]);
            });

            if (!matchesFilters) {
                return false;
            }

            if (searchNeedle.length === 0) {
                return true;
            }

            const sectorLabel = groupedFilterLabelByValueByKey.Sektor?.get(row.Sektor) ?? row.Sektor;
            const regionLabel = groupedFilterLabelByValueByKey.Region?.get(row.Region) ?? row.Region;

            const searchableValues = [
                formatVedtaksaar(row.vedtaksdato),
                row.prosjekttittel,
                row.prosjekteier,
                row.Støtteobjekt,
                row.Sektor,
                sectorLabel,
                row.Region,
                regionLabel,
                row.Støtteprogram,
            ];

            return searchableValues.some((value) => normalizeSearchValue(value).includes(searchNeedle));
        });
    }, [prosjekter, deferredSearchTerm, selectedFilters, yearRange]);

    const sortedProsjekter = useMemo(() => {
        const nextRows = [...filteredProsjekter];

        nextRows.sort((a, b) => {
            const left = a[sortKey];
            const right = b[sortKey];

            if (sortKey === "vedtaksdato") {
                if (left === null && right === null) {
                    return 0;
                }

                if (left === null) {
                    return 1;
                }

                if (right === null) {
                    return -1;
                }

                const result = Number(left) - Number(right);
                return sortDirection === "asc" ? result : -result;
            }

            const result = numericSortKeys.includes(sortKey)
                ? Number(left) - Number(right)
                : String(left).localeCompare(String(right), "nb", { sensitivity: "base", numeric: true });

            return sortDirection === "asc" ? result : -result;
        });

        return nextRows;
    }, [filteredProsjekter, sortDirection, sortKey]);

    const totalPages = Math.max(1, Math.ceil(sortedProsjekter.length / PAGE_SIZE));
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const startIndex = (safeCurrentPage - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    const visibleRows = sortedProsjekter.slice(startIndex, endIndex);
    const { pages, prevButtonProps, nextButtonProps } = usePagination({
        currentPage: safeCurrentPage,
        totalPages,
        setCurrentPage,
    });
    const paginationContainerRef = useRef<HTMLDivElement>(null);

    // <ds-pagination> (designsystemet-web) rewrites aria-label on every button it contains
    // based on its own DOM-position math, independent of what we render. Two bugs come out
    // of that: (1) disabled prev/next buttons get role="none" alongside a leftover aria-label
    // (an ARIA conflict flagged by Lighthouse), and (2) enabled prev/next buttons get their
    // aria-label overwritten to the bare target page number ("2"), which no longer contains
    // their visible text ("Neste") — a label-in-name mismatch, also flagged by Lighthouse.
    // Fix both by removing the stray aria-label whenever it reappears after the web
    // component's own (debounced) attribute updates; the button's visible text then becomes
    // its accessible name.
    useEffect(() => {
        const container = paginationContainerRef.current;
        if (!container) return;

        const fixConflictingAriaLabel = (element: Element) => {
            if (element.getAttribute("role") === "none" && element.hasAttribute("aria-label")) {
                element.removeAttribute("aria-label");
                return;
            }

            const label = element.getAttribute("aria-label");
            const text = element.textContent?.trim();
            if (label && text && !label.toLocaleLowerCase("nb").includes(text.toLocaleLowerCase("nb"))) {
                element.removeAttribute("aria-label");
            }
        };

        for (const element of container.querySelectorAll("button, a")) {
            fixConflictingAriaLabel(element);
        }

        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (mutation.target instanceof Element) {
                    fixConflictingAriaLabel(mutation.target);
                }
            }
        });

        observer.observe(container, {
            attributes: true,
            attributeFilter: ["role", "aria-label"],
            subtree: true,
        });

        return () => observer.disconnect();
    }, []);

    const visibleStart = sortedProsjekter.length === 0 ? 0 : startIndex + 1;
    const visibleEnd = Math.min(endIndex, sortedProsjekter.length);

    const setSort = (nextSortKey: SortKey) => {
        startTransition(() => {
            setCurrentPage(1);

            setSortDirection((previousDirection) => {
                if (nextSortKey === sortKey) {
                    return previousDirection === "asc" ? "desc" : "asc";
                }

                return getDefaultSortDirectionForSortKey(nextSortKey);
            });

            setSortKey(nextSortKey);
        });
    };

    const setFilter = (key: FilterKey, values: SuggestionItem[]) => {
        startTransition(() => {
            setCurrentPage(1);
            setSelectedFilters((previousFilters) => ({
                ...previousFilters,
                [key]: normalizeFilterSelection(groupedFilterConfigByKey[key], values, previousFilters[key]),
            }));
        });
    };
    const prosjektCount = filteredProsjekter.length;
    const totalStottebelop = filteredProsjekter.reduce((sum, row) => sum + row.stottebelop, 0);
    const totalKlimaresultat = filteredProsjekter.reduce((sum, row) => sum + row.utslippsreduksjon, 0);
    const totalKlimaresultatInKiloton = totalKlimaresultat / 1_000;
    const selectedAvtaleperiodeText = selectedFilters.Avtaleperiode.map((item) => item.label).join(", ");
    const periodTextIn = selectedAvtaleperiodeText ? ` i perioden ${selectedAvtaleperiodeText}` : "";
    const periodTextFor = selectedAvtaleperiodeText ? ` for perioden ${selectedAvtaleperiodeText}` : "";

    const yearRangeTextBase =
        yearRange.toYear &&
        yearRange.fromYear &&
        (yearRange.toYear !== maxAvailableYear || yearRange.fromYear !== minAvailableYear)
            ? yearRange.toYear > yearRange.fromYear
                ? ` vedtaksår ${yearRange.fromYear}–${yearRange.toYear}`
                : yearRange.toYear === yearRange.fromYear
                  ? ` i vedtaksår ${yearRange.fromYear}`
                  : ""
            : "";
    const yearRangeText =
        periodTextIn || periodTextFor
            ? yearRangeTextBase
                ? `, ${yearRangeTextBase}`
                : ""
            : yearRangeTextBase
              ? ` i ${yearRangeTextBase}`
              : "";

    const clearAllFilters = () => {
        startTransition(() => {
            setCurrentPage(1);
            setSelectedFilters(emptyFilterState);
            setYearRange({ fromYear: minAvailableYear, toYear: maxAvailableYear });
        });
    };

    const handleDownloadCsv = () => {
        const dateStamp = new Date().toISOString().slice(0, 10);
        downloadProsjektCsv(`prosjektliste-${dateStamp}.csv`, sortedProsjekter, ",");
    };

    const handleDownloadExcel = () => {
        const dateStamp = new Date().toISOString().slice(0, 10);
        downloadProsjektExcel(`prosjektliste-${dateStamp}.xlsx`, sortedProsjekter);
    };

    const openRowUrl = (url?: string) => {
        if (!url || typeof window === "undefined") {
            return;
        }

        window.location.assign(url);
    };

    return (
        <AreaContainer>
            <Dialog>
                <FlexContainer flexDirection="column" gap="var(--ds-size-4)" alignItems="flex-start">
                    <Heading level={3} data-size="xs">
                        Teknisk levetid
                    </Heading>
                    <Paragraph>
                        Teknisk levetid er hvor lenge en teknologi eller løsning forventes å virke og gi effekt etter at
                        den tas i bruk. Den brukes til å regne ut den totale direkte utslippseffekten over tid, for
                        eksempel ved å multiplisere årlig utslippseffekt med levetiden. Teknisk levetid er vanligvis
                        satt per støtteprogram og ikke for hvert enkelt prosjekt. Derfor kan verdien avvike fra andre
                        kilder.
                    </Paragraph>
                    <Heading level={3} data-size="xs">
                        Direkte utslippsreduksjon over levetid
                    </Heading>
                    <Paragraph>
                        Dette tallet viser estimerte utslipp som unngås som følge av prosjektene, samlet over hele
                        perioden løsningene skal være i bruk. Siden løsningene er i drift i ulikt antall år, vil en
                        felles årlig sum for alle prosjektene gi et feil bilde. Derfor viser vi den totale
                        utslippsreduksjonen i boksen under. Men laster du ned tabellen, får du også tall for årlig
                        utslippsreduksjon for hvert enkelt prosjekt.
                    </Paragraph>
                    <Paragraph>
                        Husk at dette kun viser den direkte effekten av selve prosjektene. Enovas totale bidrag til
                        utslippsreduksjon er betydelig større, og skapes gjennom varige markedsendringer og langsiktig
                        omstilling til lavutslippssamfunnet. Tallet er oppgitt i tonn CO₂e, altså i tonn
                        CO₂‑ekvivalenter.
                    </Paragraph>
                    <Heading level={3} data-size="xs">
                        Energiresultat
                    </Heading>
                    <Paragraph>
                        Energiresultat viser enten hvor mye ny fornybar energi prosjektet produserer, eller hvor mye
                        fornybar energi som spares gjennom mer effektiv bruk. Resultatet måles i kilowattimer (kWh) per
                        år.
                    </Paragraph>
                    <Paragraph>
                        Fra avtaleperioden 2025 til 2028 endret Enova måten energiresultat beregnes på. Målet er nå å
                        vise hvor mye ny fornybar energi som faktisk kommer til, eller hvor mye fornybart energiforbruk
                        som faktisk blir redusert.
                    </Paragraph>
                    <Paragraph>
                        Elektrifisering teller ikke lenger med i energiresultatet. Å bytte til elektriske løsninger øker
                        nemlig behovet for fornybar kraft, i stedet for å redusere det. Derfor kan ikke
                        energiresultatene i denne lista sammenlignes direkte med rapporterte resultater fra tidligere
                        avtaleperioder.
                    </Paragraph>
                    <Heading level={3} data-size="xs">
                        Hvorfor har noen prosjekter null i feltet for utslippsreduksjon eller energiresultat?
                    </Heading>
                    <Paragraph>
                        Årsaken til det er at Enova gir støtte til prosjekter som skal fremskynde utviklingen og
                        utbredelsen av løsninger innen både klima- og energiomstilling. Dermed er det noen av
                        prosjektene Enova gir støtte til som kun har utslippsresultat, mens andre prosjekter har kun
                        energiresultat. Noen prosjekter, f.eks. innen støtte til biogass, har begge deler. Enova gir
                        også støtte til innovative prosjekter som muliggjør en effektiv klima- og energiomstilling i
                        tråd med lavutslippssamfunnet.
                    </Paragraph>
                    <Paragraph>
                        Noen av disse prosjektene har verken oppgitt klima- eller energiresultat i prosjektlisten. Dette
                        er prosjekter som trenger mer modning, og er viktige å støtte fordi de på lengre sikt kan oppnå
                        betydelige resultater. Dette gjelder for eksempel innen karbonfangst.
                    </Paragraph>

                    <Paragraph data-size="sm" style={{ fontStyle: "italic", marginTop: "var(--ds-size-6)" }}>
                        Dette verktøyet er under løpende utvikling. Det pågår kontinuerlig arbeid med å forbedre
                        kvaliteten på data og funksjonalitet i løsningen. Dersom du oppdager feil eller mangler, eller
                        har forslag til forbedringer, kan du{" "}
                        <a
                            href="https://share-eu1.hsforms.com/1sPpEVW8pQY-TLurYNG_Vgw4jy0j"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            kontakte oss her
                        </a>{" "}
                        (kontaktskjema åpner i ny fane). Vi setter stor pris på tilbakemeldinger.
                    </Paragraph>
                </FlexContainer>
            </Dialog>

            <FlexContainer flexDirection="column" gap="var(--ds-size-18)" marginBottom="var(--ds-size-10)">
                <Filter
                    searchTerm={searchTerm}
                    selectedFilters={selectedFilters}
                    filterOptions={filterOptions}
                    groupedFilterConfigByKey={groupedFilterConfigByKey}
                    minAvailableYear={minAvailableYear}
                    maxAvailableYear={maxAvailableYear}
                    yearRange={yearRange}
                    onSearchTermChange={(nextSearchTerm) => {
                        setCurrentPage(1);
                        setSearchTerm(nextSearchTerm);
                    }}
                    onFilterChange={setFilter}
                    onYearRangeChange={({ fromYear, toYear }) => {
                        startTransition(() => {
                            setCurrentPage(1);
                            setYearRange({ fromYear, toYear });
                        });
                    }}
                    onClearAllFilters={clearAllFilters}
                    onDownloadCsv={handleDownloadCsv}
                    onDownloadExcel={handleDownloadExcel}
                />

                <section style={{ width: "100%" }}>
                    <FlexContainer
                        gap="var(--ds-size-8)"
                        justifyContent="space-between"
                        flexWrap="wrap"
                        alignItems="flex-start"
                    >
                        <Card data-variant="tinted" data-color="fjordgronn" className="prosjektliste-summary-card">
                            <Card.Block>
                                <FlexContainer
                                    flexDirection="column"
                                    gap="var(--ds-size-2)"
                                    justifyContent="flex-start"
                                    alignItems="flex-start"
                                >
                                    <Tag data-color="fjordgronn" variant="outline">
                                        Antall
                                    </Tag>
                                    <Heading level={2} data-size="lg">
                                        {formatCardNumber(prosjektCount)} prosjekter
                                    </Heading>
                                    <Paragraph data-size="sm">
                                        har fått tilsagn om støtte{periodTextIn}
                                        {yearRangeText}
                                    </Paragraph>
                                </FlexContainer>
                            </Card.Block>
                        </Card>
                        <Card data-variant="tinted" data-color="fjellgronn" className="prosjektliste-summary-card">
                            <Card.Block>
                                <FlexContainer
                                    flexDirection="column"
                                    gap="var(--ds-size-2)"
                                    justifyContent="flex-start"
                                    alignItems="flex-start"
                                >
                                    <Tag data-color="fjellgronn" variant="outline">
                                        Beløp
                                    </Tag>
                                    <Heading level={2} data-size="lg">
                                        {formatBelop(totalStottebelop)}
                                    </Heading>
                                    <Paragraph data-size="sm">
                                        er tildelt i støtte{periodTextFor}
                                        {yearRangeText}
                                    </Paragraph>
                                </FlexContainer>
                            </Card.Block>
                        </Card>
                        <Card data-variant="tinted" data-color="sjoblaa" className="prosjektliste-summary-card">
                            <Card.Block>
                                <FlexContainer
                                    flexDirection="column"
                                    gap="var(--ds-size-2)"
                                    justifyContent="flex-start"
                                    alignItems="flex-start"
                                >
                                    <Tag data-color="sjoblaa" variant="outline">
                                        Direkte utslippsreduksjon over levetid
                                    </Tag>
                                    <Heading level={2} data-size="lg">
                                        {formatCardNumber(totalKlimaresultatInKiloton)} tonn CO₂e
                                    </Heading>
                                    <Paragraph data-size="sm">
                                        er estimert for prosjektene{periodTextIn}
                                        {yearRangeText}
                                    </Paragraph>
                                </FlexContainer>
                            </Card.Block>
                        </Card>
                    </FlexContainer>
                </section>
            </FlexContainer>

            <Paragraph data-size="xs" className="prosjektliste-responsive-table-scroll-hint">
                Scroll tabellen horisontalt for å se alle kolonner.
            </Paragraph>

            <section style={{ width: "100%", overflowX: "auto", marginBottom: "var(--ds-size-10)" }}>
                <Table className="prosjektliste-responsive-table" border>
                    <caption className="sr-only">Tabell med prosjektdata</caption>
                    <Table.Head>
                        <Table.Row>
                            {sortableColumns.map((column) => {
                                const isActive = sortKey === column.key;
                                const ariaSort = isActive
                                    ? sortDirection === "asc"
                                        ? "ascending"
                                        : "descending"
                                    : "none";

                                return (
                                    <Table.HeaderCell
                                        key={column.key}
                                        sort={ariaSort}
                                        data-is-numeric={column.isNumeric ? "true" : "false"}
                                        onClick={() => {
                                            setSort(column.key);
                                        }}
                                    >
                                        {column.tooltipText ? (
                                            <span data-tooltip={column.tooltipText}>
                                                <span className="prosjektliste-header-label-full">{column.label}</span>
                                                {column.shortLabel && (
                                                    <span className="prosjektliste-header-label-short">
                                                        {column.shortLabel}
                                                    </span>
                                                )}
                                                <span
                                                    aria-hidden="true"
                                                    className="prosjektliste-responsive-table-header-tooltip-icon"
                                                >
                                                    <InformationIcon aria-hidden="true" fontSize="1.25rem" />
                                                </span>
                                            </span>
                                        ) : (
                                            <>
                                                <span className="prosjektliste-header-label-full">{column.label}</span>
                                                {column.shortLabel && (
                                                    <span className="prosjektliste-header-label-short">
                                                        {column.shortLabel}
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </Table.HeaderCell>
                                );
                            })}
                        </Table.Row>
                    </Table.Head>
                    <Table.Body>
                        {visibleRows.map((row, rowIndex) => (
                            <Table.Row
                                key={`${row.saksnr}-${row.prosjekteier}-${startIndex + rowIndex}`}
                                className={row.url ? "prosjektliste-clickable-row" : undefined}
                                onClick={() => {
                                    openRowUrl(row.url);
                                }}
                                onKeyDown={(event) => {
                                    if (!row.url) {
                                        return;
                                    }

                                    if (event.key === "Enter" || event.key === " ") {
                                        event.preventDefault();
                                        openRowUrl(row.url);
                                    }
                                }}
                                tabIndex={row.url ? 0 : undefined}
                                aria-label={row.url ? `Åpne prosjekt ${row.prosjekttittel}` : undefined}
                            >
                                {sortableColumns.map((column) => {
                                    const isCancelled = row.Status === "Kansellert";

                                    if (column.key === "stottebelop" && shouldShowOriginalSupportAmount(row)) {
                                        return (
                                            <Table.Cell
                                                key={column.key}
                                                data-label={column.label}
                                                data-is-numeric={column.isNumeric ? "true" : "false"}
                                            >
                                                <span className="prosjektliste-original-support-value">
                                                    <span>0</span>
                                                    <span className="prosjektliste-original-support-row">
                                                        <span className="prosjektliste-original-support-amount">
                                                            {tableNumberFormatter.format(row.stottebelopOpprinnelig)}
                                                        </span>
                                                        <InfoTooltip
                                                            text={`Prosjektet er kansellert. Opprinnelig vedtatt støttebeløp var ${tableNumberFormatter.format(
                                                                row.stottebelopOpprinnelig,
                                                            )} kr.`}
                                                            withBg
                                                        />
                                                    </span>
                                                </span>
                                            </Table.Cell>
                                        );
                                    }

                                    const isCancelledResultColumn = column.key === "stottebelop";

                                    if (isCancelled && isCancelledResultColumn) {
                                        const cancelledColumnKey = column.key as "stottebelop";
                                        // const cancelledColumnKey = column.key as
                                        //     | "stottebelop"
                                        //     | "utslippsreduksjon"
                                        //     | "energiresultat";
                                        const originalValue = Number(row[column.key]);

                                        return (
                                            <Table.Cell
                                                key={column.key}
                                                data-label={column.label}
                                                data-is-numeric={column.isNumeric ? "true" : "false"}
                                            >
                                                <Tooltip
                                                    content={getCancelledCellTooltip(cancelledColumnKey, originalValue)}
                                                    placement="top"
                                                >
                                                    <span className="prosjektliste-cancelled-value">
                                                        <span className="prosjektliste-cancelled-original">
                                                            {tableNumberFormatter.format(originalValue)}
                                                        </span>
                                                        <span>0</span>
                                                    </span>
                                                </Tooltip>
                                            </Table.Cell>
                                        );
                                    }

                                    if (column.key === "stotteobjekter") {
                                        const supportObjectBreakdown =
                                            stotteobjektBreakdownBySaksnr.get(row.saksnr) ?? [];
                                        const totalFromBreakdown = supportObjectBreakdown.reduce(
                                            (sum, [, count]) => sum + count,
                                            0,
                                        );
                                        const displayTotal =
                                            totalFromBreakdown > 0 ? totalFromBreakdown : Number(row.stotteobjekter);

                                        return (
                                            <Table.Cell
                                                key={column.key}
                                                data-label={column.label}
                                                data-is-numeric={column.isNumeric ? "true" : "false"}
                                            >
                                                {Number.isFinite(displayTotal) && displayTotal > 0 ? (
                                                    <span className="prosjektliste-support-object-breakdown">
                                                        <span>{tableNumberFormatter.format(displayTotal)}</span>
                                                        {supportObjectBreakdown.map(([type, count]) => (
                                                            <span
                                                                className="prosjektliste-support-object-line"
                                                                key={`${row.saksnr}-${type}`}
                                                            >
                                                                {`${type}: ${tableNumberFormatter.format(count)}`}
                                                            </span>
                                                        ))}
                                                    </span>
                                                ) : (
                                                    ""
                                                )}
                                            </Table.Cell>
                                        );
                                    }

                                    return (
                                        <Table.Cell
                                            key={column.key}
                                            data-label={column.label}
                                            data-is-numeric={column.isNumeric ? "true" : "false"}
                                        >
                                            {column.key === "vedtaksdato"
                                                ? formatVedtaksaar(row.vedtaksdato)
                                                : column.isNumeric
                                                  ? column.key === "levetid" &&
                                                    (row[column.key] === 0 || row[column.key] === null)
                                                      ? ""
                                                      : tableNumberFormatter.format(Number(row[column.key]))
                                                  : row[column.key]}
                                        </Table.Cell>
                                    );
                                })}
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>

                <Paragraph data-size="sm" role="status" style={{ marginTop: "var(--ds-size-6)" }}>
                    Viser {visibleStart}-{visibleEnd} av {sortedProsjekter.length}
                </Paragraph>
            </section>

            <div ref={paginationContainerRef} style={{ display: "contents" }}>
                <Pagination
                    aria-label="Paginering for prosjektliste"
                    data-current={String(safeCurrentPage)}
                    data-total={String(totalPages)}
                    style={{ display: "flex", justifyContent: "center", width: "100%" }}
                >
                    <Pagination.List>
                        <Pagination.Item>
                            <Pagination.Button {...prevButtonProps}>Forrige</Pagination.Button>
                        </Pagination.Item>

                        {pages.map(({ page, itemKey, buttonProps }) => (
                            <Pagination.Item key={itemKey}>
                                {buttonProps ? (
                                    <Pagination.Button
                                        {...buttonProps}
                                        aria-label={typeof page === "number" ? `Side ${page}` : undefined}
                                    >
                                        {page}
                                    </Pagination.Button>
                                ) : (
                                    /* <ds-pagination> recomputes aria-label/role/tabindex/value for every
                                       button/anchor it contains, based on its own position count. A <span>
                                       ellipsis breaks that count, misaligning every button after it —
                                       rendering an inert button instead keeps the count in sync so real
                                       page buttons don't get miscomputed as role="none". */
                                    <Pagination.Button aria-hidden="true" aria-disabled="true" tabIndex={-1}>
                                        ...
                                    </Pagination.Button>
                                )}
                            </Pagination.Item>
                        ))}

                        <Pagination.Item>
                            <Pagination.Button {...nextButtonProps}>Neste</Pagination.Button>
                        </Pagination.Item>
                    </Pagination.List>
                </Pagination>
            </div>
        </AreaContainer>
    );
}

export default Prosjektliste;
